import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getArticles,
    getArticleNextNumber,
    createArticle,
    getCustomers,
    getWorkOrders,
    saveWorkOrder,
    deleteWorkOrder,
} from "../../../api";
import { logError } from "../../../logging/loggingService";
import {
    buildCustomerOptions,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
} from "../../shared/customerSelectUtils";
import {
    buildOrderPayload,
    createEmptyOrder,
    mapDtoToOrder,
    orderTotals,
    resolveInvoiceStatus,
    resolvePerformanceAmount,
    toNum,
} from "./orderManagementUtils";

const createEmptyPerformance = () => ({
    id: null,
    version: null,
    date: "",
    articleId: "",
    articleName: "",
    amount: "",
    start: "",
    end: "",
    invoiced: false,
});

const createEmptyMaterial = () => ({
    id: null,
    version: null,
    articleId: "",
    articleName: "",
    amount: "",
    invoiced: false,
});

const createEmptyArticle = () => ({
    articleNumber: "",
    name: "",
    price: "",
    taxRate: 19,
});

export default function useOrderManagement() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [savingOrder, setSavingOrder] = useState(false);
    const [deletingOrder, setDeletingOrder] = useState(false);
    const [orderErrors, setOrderErrors] = useState({});
    const [performanceErrors, setPerformanceErrors] = useState({});
    const [materialErrors, setMaterialErrors] = useState({});
    const [performanceEntryErrors, setPerformanceEntryErrors] = useState({});
    const [materialEntryErrors, setMaterialEntryErrors] = useState({});
    const [newArticleErrors, setNewArticleErrors] = useState({});

    const [openDialog, setOpenDialog] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [currentOrder, setCurrentOrder] = useState(createEmptyOrder);

    const [openPerformanceDialog, setOpenPerformanceDialog] = useState(false);
    const [editingPerformanceIndex, setEditingPerformanceIndex] = useState(null);
    const [currentPerformance, setCurrentPerformance] = useState(createEmptyPerformance);

    const [openMaterialDialog, setOpenMaterialDialog] = useState(false);
    const [editingMaterialIndex, setEditingMaterialIndex] = useState(null);
    const [currentMaterial, setCurrentMaterial] = useState(createEmptyMaterial);

    const [articles, setArticles] = useState([]);
    const [loadingArticles, setLoadingArticles] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [openNewArticleDialog, setOpenNewArticleDialog] = useState(false);
    const [newArticle, setNewArticle] = useState(createEmptyArticle);
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
    const [invoiceOrder, setInvoiceOrder] = useState(null);
    const [invoiceSelections, setInvoiceSelections] = useState({ performance: [], materials: [] });

    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const showSnack = useCallback((msg, sev = "success") => setSnack({ open: true, msg, sev }), []);

    const fetchArticles = useCallback(() => {
        setLoadingArticles(true);
        getArticles()
            .then((res) => setArticles(res.data || []))
            .catch((err) => {
                logError("Fehler beim Laden der Artikel", err, { component: "OrderManagement" });
                showSnack("Fehler beim Laden der Artikel", "error");
            })
            .finally(() => setLoadingArticles(false));
    }, [showSnack]);

    const fetchCustomers = useCallback(() => {
        setLoadingCustomers(true);
        getCustomers()
            .then((res) => setCustomers(res.data || []))
            .catch((err) => {
                logError("Fehler beim Laden der Kunden", err, { component: "OrderManagement" });
                showSnack("Fehler beim Laden der Kunden", "error");
            })
            .finally(() => setLoadingCustomers(false));
    }, [showSnack]);

    const fetchOrders = useCallback(() => {
        setLoadingOrders(true);
        getWorkOrders()
            .then((res) => {
                const payload = Array.isArray(res.data) ? res.data : [];
                setOrders(payload.map(mapDtoToOrder));
            })
            .catch((err) => {
                logError("Fehler beim Laden der Aufträge", err, { component: "OrderManagement" });
                showSnack("Fehler beim Laden der Aufträge", "error");
            })
            .finally(() => setLoadingOrders(false));
    }, [showSnack]);

    useEffect(() => {
        fetchArticles();
        fetchCustomers();
        fetchOrders();
    }, [fetchArticles, fetchCustomers, fetchOrders]);

    const customerOptions = useMemo(() => buildCustomerOptions(customers), [customers]);

    const resolveCustomerLabel = (customerId) => {
        if (!customerId) return "";
        const match = customerOptions.find((option) => option.id === customerId);
        return match ? match.label : "";
    };

    const selectedCustomerOption = useMemo(() => {
        if (!currentOrder.customerId) return null;
        return customerOptions.find((option) => option.id === currentOrder.customerId) || null;
    }, [currentOrder.customerId, customerOptions]);

    const handleDialogOpen = () => {
        setCurrentOrder(createEmptyOrder());
        setEditingIndex(null);
        setOrderErrors({});
        setPerformanceEntryErrors({});
        setMaterialEntryErrors({});
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        if (savingOrder) return;
        setOpenDialog(false);
    };

    const handleOrderFieldChange = (field, value) => {
        setCurrentOrder((prev) => ({ ...prev, [field]: value }));
        setOrderErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleCustomerChange = (_, value) => {
        setCurrentOrder((order) => ({
            ...order,
            customerId: value?.id || "",
            customer: value?.label || "",
        }));
        setOrderErrors((prev) => {
            if (!prev.customerId) return prev;
            const next = { ...prev };
            delete next.customerId;
            return next;
        });
    };

    const handlePerformanceFieldChange = (field, value) => {
        setCurrentPerformance((prev) => ({ ...prev, [field]: value }));
        setPerformanceErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handlePerformanceArticleChange = (articleId) => {
        const matched = articles.find((article) => article?.id === articleId);
        setCurrentPerformance((prev) => ({
            ...prev,
            articleId,
            articleName: matched?.name || prev.articleName || "",
        }));
        setPerformanceErrors((prev) => {
            if (!prev.articleId) return prev;
            const next = { ...prev };
            delete next.articleId;
            return next;
        });
    };

    const handleMaterialFieldChange = (field, value) => {
        setCurrentMaterial((prev) => ({ ...prev, [field]: value }));
        setMaterialErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleMaterialArticleChange = (articleId) => {
        const matched = articles.find((article) => article?.id === articleId);
        setCurrentMaterial((prev) => ({
            ...prev,
            articleId,
            articleName: matched?.name || prev.articleName || "",
        }));
        setMaterialErrors((prev) => {
            if (!prev.articleId) return prev;
            const next = { ...prev };
            delete next.articleId;
            return next;
        });
    };

    const handleNewArticleFieldChange = (field, value) => {
        setNewArticle((prev) => ({ ...prev, [field]: value }));
        setNewArticleErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleSaveOrder = async () => {
        if (savingOrder) return;
        const trimmedTitle = currentOrder.title.trim();
        if (!currentOrder.customerId || !trimmedTitle) {
            const nextErrors = {};
            if (!currentOrder.customerId) {
                nextErrors.customerId = "Bitte Kunden auswaehlen.";
            }
            if (!trimmedTitle) {
                nextErrors.title = "Bitte Beschreibung angeben.";
            }
            setOrderErrors(nextErrors);
            showSnack("Bitte Kunden auswaehlen und Beschreibung ausfuellen.", "warning");
            return;
        }
        setOrderErrors({});
        const indexToUpdate = editingIndex;
        const isEdit = indexToUpdate !== null;
        const orderSnapshot = { ...currentOrder, title: trimmedTitle };
        const customerLabel = orderSnapshot.customer || resolveCustomerLabel(orderSnapshot.customerId);

        setSavingOrder(true);
        try {
            const response = await saveWorkOrder(buildOrderPayload(orderSnapshot, { includeEntries: true }));
            const savedOrder = mapDtoToOrder(response?.data || {});

            const mergedOrder = {
                ...savedOrder,
                customer: customerLabel,
            };
            setOrders((prev) => {
                if (isEdit && indexToUpdate !== null) {
                    const updated = [...prev];
                    updated[indexToUpdate] = mergedOrder;
                    return updated;
                }
                return [...prev, mergedOrder];
            });
            showSnack(isEdit ? "Auftrag aktualisiert" : "Auftrag gespeichert");
            setEditingIndex(null);
            setCurrentOrder(createEmptyOrder());
            setOpenDialog(false);
            setPerformanceEntryErrors({});
            setMaterialEntryErrors({});
        } catch (err) {
            logError("Fehler beim Speichern des Auftrags", err, { component: "OrderManagement" });
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const nextOrderErrors = {};
                const nextPerformanceErrors = {};
                const nextMaterialErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const timeMatch = /^timeEntries\[(\d+)\]\.(.+)$/.exec(rawField);
                    if (timeMatch) {
                        const index = Number(timeMatch[1]);
                        const field = timeMatch[2];
                        nextPerformanceErrors[index] = {
                            ...(nextPerformanceErrors[index] || {}),
                            [field]: message,
                        };
                        return;
                    }
                    const materialMatch = /^materialEntries\[(\d+)\]\.(.+)$/.exec(rawField);
                    if (materialMatch) {
                        const index = Number(materialMatch[1]);
                        const field = materialMatch[2];
                        nextMaterialErrors[index] = {
                            ...(nextMaterialErrors[index] || {}),
                            [field]: message,
                        };
                        return;
                    }
                    nextOrderErrors[rawField] = message;
                });
                setOrderErrors(nextOrderErrors);
                setPerformanceEntryErrors(nextPerformanceErrors);
                setMaterialEntryErrors(nextMaterialErrors);
                showSnack(backendError?.message || "Bitte prüfe die markierten Felder.", "error");
                return;
            }
            showSnack("Fehler beim Speichern des Auftrags", "error");
        } finally {
            setSavingOrder(false);
        }
    };

    const handleEditOrder = (index) => {
        const order = orders[index];
        if (!order) return;
        const customerLabel = order.customer || resolveCustomerLabel(order.customerId);
        setCurrentOrder({
            ...order,
            customerId: order.customerId || "",
            customer: customerLabel || "",
        });
        setEditingIndex(index);
        setOrderErrors({});
        setPerformanceEntryErrors({});
        setMaterialEntryErrors({});
        setOpenDialog(true);
    };

    const handleDeleteOrder = (index) => {
        const order = orders[index];
        if (!order) return;

        if (!order.id) {
            setOrders((o) => o.filter((_, i) => i !== index));
            showSnack("Auftrag gelöscht", "info");
            return;
        }

        setDeletingOrder(true);
        deleteWorkOrder(order.id)
            .then(() => {
                setOrders((o) => o.filter((_, i) => i !== index));
                showSnack("Auftrag gelöscht", "info");
            })
            .catch((err) => {
                logError("Fehler beim Löschen des Auftrags", err, { component: "OrderManagement" });
                showSnack("Fehler beim Löschen des Auftrags", "error");
            })
            .finally(() => setDeletingOrder(false));
    };

    const handleOpenPerformanceDialog = () => {
        setCurrentPerformance(createEmptyPerformance());
        setEditingPerformanceIndex(null);
        setPerformanceErrors({});
        setOpenPerformanceDialog(true);
    };

    const handleClosePerformanceDialog = () => {
        setOpenPerformanceDialog(false);
    };

    const handleEditPerformance = (index) => {
        const entry = currentOrder.performanceDays[index];
        if (!entry) return;
        setCurrentPerformance({
            id: entry.id ?? null,
            version: entry.version ?? null,
            date: entry.date ?? "",
            articleId: entry.articleId ?? "",
            articleName: entry.articleName ?? "",
            amount: entry.amount ?? "",
            start: entry.start ?? "",
            end: entry.end ?? "",
            invoiced: entry.invoiced ?? false,
        });
        setEditingPerformanceIndex(index);
        setPerformanceErrors(performanceEntryErrors[index] || {});
        setOpenPerformanceDialog(true);
    };

    const handleSavePerformance = () => {
        const { date, articleId, amount } = currentPerformance;
        if (!date || !articleId || !amount) {
            const nextErrors = {};
            if (!date) nextErrors.date = "Bitte Datum angeben.";
            if (!articleId) nextErrors.articleId = "Bitte Artikel auswählen.";
            if (!amount) nextErrors.amount = "Bitte Menge angeben.";
            setPerformanceErrors(nextErrors);
            showSnack("Bitte Datum, Artikel und Menge angeben.", "warning");
            return;
        }
        const selectedArticle = articles.find((article) => article?.id === articleId);
        const updated = [...currentOrder.performanceDays];
        if (editingPerformanceIndex !== null) {
            updated[editingPerformanceIndex] = {
                ...currentPerformance,
                articleName: currentPerformance.articleName || selectedArticle?.name || "",
            };
        } else {
            updated.push({
                ...currentPerformance,
                articleName: currentPerformance.articleName || selectedArticle?.name || "",
            });
        }
        setCurrentOrder((order) => ({ ...order, performanceDays: updated }));
        setOpenPerformanceDialog(false);
        setEditingPerformanceIndex(null);
        setPerformanceErrors({});
        setPerformanceEntryErrors((prev) => {
            if (editingPerformanceIndex === null || prev[editingPerformanceIndex] === undefined) return prev;
            const next = { ...prev };
            delete next[editingPerformanceIndex];
            return next;
        });
        showSnack("Leistung gespeichert");
    };

    const handleRemovePerformance = (index) => {
        setCurrentOrder((order) => ({
            ...order,
            performanceDays: order.performanceDays.filter((_, i) => i !== index),
        }));
        setPerformanceEntryErrors({});
        showSnack("Leistung entfernt", "info");
    };

    const handleOpenMaterialDialog = () => {
        setCurrentMaterial(createEmptyMaterial());
        setEditingMaterialIndex(null);
        setMaterialErrors({});
        setOpenMaterialDialog(true);
    };

    const handleCloseMaterialDialog = () => {
        setOpenMaterialDialog(false);
    };

    const handleEditMaterial = (index) => {
        const entry = currentOrder.materials[index];
        if (!entry) return;
        setCurrentMaterial({
            id: entry.id ?? null,
            version: entry.version ?? null,
            articleId: entry.articleId ?? "",
            articleName: entry.articleName ?? "",
            amount: entry.amount ?? "",
            invoiced: entry.invoiced ?? false,
        });
        setEditingMaterialIndex(index);
        setMaterialErrors(materialEntryErrors[index] || {});
        setOpenMaterialDialog(true);
    };

    const handleSaveMaterial = () => {
        const { articleId, amount } = currentMaterial;
        if (!articleId || !amount) {
            const nextErrors = {};
            if (!articleId) nextErrors.articleId = "Bitte Artikel auswählen.";
            if (!amount) nextErrors.amount = "Bitte Menge angeben.";
            setMaterialErrors(nextErrors);
            showSnack("Bitte Artikel und Menge angeben.", "warning");
            return;
        }
        const selectedArticle = articles.find((article) => article?.id === articleId);
        const updated = [...currentOrder.materials];
        if (editingMaterialIndex !== null) {
            updated[editingMaterialIndex] = {
                ...currentMaterial,
                articleName: currentMaterial.articleName || selectedArticle?.name || "",
            };
        } else {
            updated.push({
                ...currentMaterial,
                articleName: currentMaterial.articleName || selectedArticle?.name || "",
            });
        }
        setCurrentOrder((order) => ({ ...order, materials: updated }));
        setOpenMaterialDialog(false);
        setEditingMaterialIndex(null);
        setMaterialErrors({});
        setMaterialEntryErrors((prev) => {
            if (editingMaterialIndex === null || prev[editingMaterialIndex] === undefined) return prev;
            const next = { ...prev };
            delete next[editingMaterialIndex];
            return next;
        });
        showSnack("Material gespeichert");
    };

    const handleRemoveMaterial = (index) => {
        setCurrentOrder((order) => ({
            ...order,
            materials: order.materials.filter((_, i) => i !== index),
        }));
        setMaterialEntryErrors({});
        showSnack("Material entfernt", "info");
    };

    const handleOpenNewArticleDialog = async () => {
        try {
            const res = await getArticleNextNumber();
            setNewArticle({
                articleNumber: res.data,
                name: "",
                price: "",
                taxRate: 19,
            });
            setNewArticleErrors({});
            setOpenNewArticleDialog(true);
        } catch (error) {
            logError("Fehler beim Abrufen der nächsten Artikelnummer", error, { component: "OrderManagement" });
            showSnack("Fehler beim Abrufen der nächsten Artikelnummer", "error");
        }
    };

    const handleCloseNewArticleDialog = () => {
        setOpenNewArticleDialog(false);
    };

    const handleSaveNewArticle = () => {
        const body = {
            ...newArticle,
            price: toNum(newArticle.price),
            taxRate: toNum(newArticle.taxRate),
        };
        if (!body.name) {
            setNewArticleErrors({ name: "Bitte Artikelname angeben." });
            showSnack("Bitte Artikelname angeben.", "warning");
            return;
        }
        createArticle(body)
            .then((response) => {
                setArticles((list) => [...list, response.data]);
                setCurrentMaterial((material) => ({
                    ...material,
                    articleId: response.data?.id || "",
                    articleName: response.data?.name || "",
                }));
                showSnack("Artikel angelegt");
                setNewArticleErrors({});
                setOpenNewArticleDialog(false);
            })
            .catch((err) => {
                logError("Fehler beim Speichern des Artikels", err, { component: "OrderManagement" });
                showSnack("Fehler beim Speichern des Artikels", "error");
            });
    };

    const handleCreateInvoiceFromOrder = (order) => {
        if (!order) return;
        setInvoiceOrder(order);
        setInvoiceSelections({
            performance: order.performanceDays.map((entry) => !entry.invoiced),
            materials: order.materials.map((entry) => !entry.invoiced),
        });
        setInvoiceDialogOpen(true);
    };

    const handleCloseInvoiceDialog = () => {
        setInvoiceDialogOpen(false);
        setInvoiceOrder(null);
        setInvoiceSelections({ performance: [], materials: [] });
    };

    const toggleInvoiceSelection = (type, index) => {
        const entries = type === "performance" ? performanceEntries : materialEntries;
        if (entries[index]?.invoiced) return;
        setInvoiceSelections((prev) => {
            const next = [...(prev[type] || [])];
            next[index] = !next[index];
            return { ...prev, [type]: next };
        });
    };

    const toggleAllInvoiceSelections = (type, checked) => {
        const entries = type === "performance" ? performanceEntries : materialEntries;
        setInvoiceSelections((prev) => ({
            ...prev,
            [type]: (prev[type] || []).map((value, index) =>
                entries[index]?.invoiced ? false : checked
            ),
        }));
    };

    const handleConfirmInvoiceSelection = () => {
        if (!invoiceOrder) return;
        if (!invoiceOrder.customerId) {
            showSnack("Bitte zuerst einen Kunden am Auftrag hinterlegen.", "warning");
            return;
        }
        const selectedItems = [];
        let skipped = 0;

        invoiceOrder.performanceDays.forEach((entry, index) => {
            if (!invoiceSelections.performance[index]) return;
            if (entry.invoiced) {
                skipped += 1;
                return;
            }
            const amount = resolvePerformanceAmount(entry);
            if (!entry.id || !entry.articleId || amount <= 0) {
                skipped += 1;
                return;
            }
            selectedItems.push({
                articleId: entry.articleId,
                amount,
                articleName: entry.articleName || "",
                workOrderTimeEntryId: entry.id,
            });
        });

        invoiceOrder.materials.forEach((entry, index) => {
            if (!invoiceSelections.materials[index]) return;
            if (entry.invoiced) {
                skipped += 1;
                return;
            }
            const amount = toNum(entry.amount);
            if (!entry.id || !entry.articleId || amount <= 0) {
                skipped += 1;
                return;
            }
            selectedItems.push({
                articleId: entry.articleId,
                amount,
                articleName: entry.articleName || "",
                workOrderMaterialEntryId: entry.id,
            });
        });

        if (selectedItems.length === 0) {
            showSnack("Bitte mindestens eine Position auswaehlen.", "warning");
            return;
        }

        if (skipped > 0) {
            showSnack("Einige Positionen konnten nicht uebernommen werden.", "warning");
        }

        setInvoiceDialogOpen(false);
        navigate("/invoices", {
            state: {
                invoicePrefill: {
                    orderId: invoiceOrder.id || "",
                    customerId: invoiceOrder.customerId || "",
                    items: selectedItems,
                },
            },
        });
    };

    const performanceEntries = invoiceOrder?.performanceDays || [];
    const materialEntries = invoiceOrder?.materials || [];
    const selectedPerformanceCount = performanceEntries.reduce((count, entry, index) => {
        if (entry.invoiced) return count;
        return invoiceSelections.performance[index] ? count + 1 : count;
    }, 0);
    const selectedMaterialCount = materialEntries.reduce((count, entry, index) => {
        if (entry.invoiced) return count;
        return invoiceSelections.materials[index] ? count + 1 : count;
    }, 0);
    const hasInvoiceSelections = selectedPerformanceCount + selectedMaterialCount > 0;
    const selectablePerformanceCount = performanceEntries.filter((entry) => !entry.invoiced).length;
    const selectableMaterialCount = materialEntries.filter((entry) => !entry.invoiced).length;
    const allPerformanceSelected =
        selectablePerformanceCount > 0 &&
        performanceEntries.every((entry, index) => entry.invoiced || invoiceSelections.performance[index]);
    const somePerformanceSelected = performanceEntries.some(
        (entry, index) => !entry.invoiced && invoiceSelections.performance[index]
    );
    const allMaterialSelected =
        selectableMaterialCount > 0 &&
        materialEntries.every((entry, index) => entry.invoiced || invoiceSelections.materials[index]);
    const someMaterialSelected = materialEntries.some(
        (entry, index) => !entry.invoiced && invoiceSelections.materials[index]
    );

    return {
        orders,
        loadingOrders,
        savingOrder,
        deletingOrder,
        orderErrors,
        performanceErrors,
        materialErrors,
        performanceEntryErrors,
        materialEntryErrors,
        newArticleErrors,
        openDialog,
        editingIndex,
        currentOrder,
        openPerformanceDialog,
        editingPerformanceIndex,
        currentPerformance,
        openMaterialDialog,
        editingMaterialIndex,
        currentMaterial,
        articles,
        loadingArticles,
        customers,
        loadingCustomers,
        openNewArticleDialog,
        newArticle,
        invoiceDialogOpen,
        invoiceOrder,
        invoiceSelections,
        snack,
        setSnack,
        customerOptions,
        selectedCustomerOption,
        handleDialogOpen,
        handleDialogClose,
        handleOrderFieldChange,
        handleCustomerChange,
        handlePerformanceFieldChange,
        handlePerformanceArticleChange,
        handleMaterialFieldChange,
        handleMaterialArticleChange,
        handleNewArticleFieldChange,
        handleSaveOrder,
        handleEditOrder,
        handleDeleteOrder,
        handleOpenPerformanceDialog,
        handleClosePerformanceDialog,
        handleEditPerformance,
        handleSavePerformance,
        handleRemovePerformance,
        handleOpenMaterialDialog,
        handleCloseMaterialDialog,
        handleEditMaterial,
        handleSaveMaterial,
        handleRemoveMaterial,
        handleOpenNewArticleDialog,
        handleCloseNewArticleDialog,
        handleSaveNewArticle,
        handleCreateInvoiceFromOrder,
        handleCloseInvoiceDialog,
        toggleInvoiceSelection,
        toggleAllInvoiceSelections,
        handleConfirmInvoiceSelection,
        fetchOrders,
        fetchArticles,
        fetchCustomers,
        resolveCustomerLabel,
        orderTotals,
        resolveInvoiceStatus,
        performanceEntries,
        materialEntries,
        selectedPerformanceCount,
        selectedMaterialCount,
        hasInvoiceSelections,
        selectablePerformanceCount,
        selectableMaterialCount,
        allPerformanceSelected,
        somePerformanceSelected,
        allMaterialSelected,
        someMaterialSelected,
        getCustomerOptionLabel,
        isCustomerOptionEqualToValue,
    };
}
